import React from 'react';
import { Box, Skeleton, Stack } from '@mui/material';

/**
 * Shared skeleton building blocks so every page's loading state uses the
 * same shapes/rhythm instead of one-off <Skeleton> combinations. All use
 * MUI's themed Skeleton (soft blue tint + wave animation, see theme.ts).
 */

// Wraps children (skeleton rows/cards) with an incremental animation-delay
// so they reveal in a gentle cascade rather than popping in all at once.
export const SkeletonStagger: React.FC<{ children: React.ReactNode; step?: number }> = ({
  children,
  step = 60,
}) => (
  <>
    {React.Children.map(children, (child, i) =>
      React.isValidElement(child) ? (
        <div className="stagger-item" style={{ animationDelay: `${i * step}ms` }}>
          {child}
        </div>
      ) : (
        child
      )
    )}
  </>
);

export const ListRowSkeleton: React.FC<{ height?: number }> = ({ height = 48 }) => (
  <Skeleton variant="rounded" animation="wave" height={height} sx={{ borderRadius: 2 }} />
);

export const CardSkeleton: React.FC<{ height?: number }> = ({ height = 120 }) => (
  <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
    <Skeleton variant="text" animation="wave" width="55%" height={22} />
    <Skeleton variant="text" animation="wave" width="80%" height={16} />
    <Skeleton variant="rounded" animation="wave" height={height - 60} sx={{ mt: 1, borderRadius: 2 }} />
  </Box>
);

export const StatCardSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: 2, borderRadius: 3 }}>
    <Skeleton variant="rounded" animation="wave" width={44} height={44} sx={{ borderRadius: '12px' }} />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" animation="wave" width="60%" height={14} />
      <Skeleton variant="text" animation="wave" width="40%" height={28} />
    </Box>
  </Box>
);

export const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 240 }) => (
  <Skeleton variant="rounded" animation="wave" width="100%" height={height} sx={{ borderRadius: 3 }} />
);

export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ rows = 5, columns = 4 }) => (
  <Stack spacing={1}>
    {Array.from({ length: rows }).map((_, r) => (
      <Stack key={r} direction="row" spacing={2}>
        {Array.from({ length: columns }).map((__, c) => (
          <Skeleton
            key={c}
            variant="text"
            animation="wave"
            height={22}
            sx={{ flex: c === 0 ? 2 : 1 }}
          />
        ))}
      </Stack>
    ))}
  </Stack>
);

export const BoardTileSkeleton: React.FC = () => (
  <Box sx={{ p: 2.5, borderRadius: '18px', border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.75 }}>
      <Skeleton variant="rounded" animation="wave" width={42} height={42} sx={{ borderRadius: '12px' }} />
      <Skeleton variant="rounded" animation="wave" width={48} height={20} sx={{ borderRadius: 999 }} />
    </Box>
    <Skeleton variant="text" animation="wave" width="70%" height={22} />
    <Skeleton variant="text" animation="wave" width="90%" height={16} />
    <Skeleton variant="text" animation="wave" width="60%" height={16} />
  </Box>
);

export const PageHeaderSkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
    <Box>
      <Skeleton variant="text" animation="wave" width={220} height={34} />
      <Skeleton variant="text" animation="wave" width={160} height={18} />
    </Box>
    <Skeleton variant="rounded" animation="wave" width={130} height={38} sx={{ borderRadius: 2 }} />
  </Box>
);

export default {
  SkeletonStagger,
  ListRowSkeleton,
  CardSkeleton,
  StatCardSkeleton,
  ChartSkeleton,
  TableSkeleton,
  BoardTileSkeleton,
  PageHeaderSkeleton,
};
